// Small helpers around the `accountInformation` object passed throughout the app.
// Centralizing role / role_extra checks here avoids spreading hard-coded strings
// like "staff", "admin", "admin_etc", "psad" across components.
//
// `accountInformation` shape (relevant fields):
//   - role:        "registered" | "staff" | "admin"
//   - role_extra:  "admin_psad" | "admin_etc" | null
//   - struct_type: "psad" | ... (string)
//   - username:    RPPS number (digits) for practitioners
//   - profession:  e.g. "doctor", "nurse", ...
//   - category:    practitioner category code (when set, user is registered as a HCP)
//   - readOnly:    true if account is in read-only mode

export const ROLES = Object.freeze({
  REGISTERED: "registered",
  STAFF: "staff",
  ADMIN: "admin",
});

export const ROLE_EXTRAS = Object.freeze({
  ADMIN_PSAD: "admin_psad",
  ADMIN_ETC: "admin_etc",
});

const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

export const userHasRole = (acc, role) => {
  if (!acc) return false;
  return norm(acc.role) === norm(role);
};

export const userHasRoleExtra = (acc, roleExtra) => {
  if (!acc) return false;
  return norm(acc.role_extra) === norm(roleExtra);
};

export const userIsRegistered = (acc) => userHasRole(acc, ROLES.REGISTERED);
export const userIsStaff      = (acc) => userHasRole(acc, ROLES.STAFF);
export const userIsAdmin      = (acc) => userHasRole(acc, ROLES.ADMIN);

export const userIsSuperAdmin = (acc) =>
  userIsAdmin(acc) && userHasRoleExtra(acc, ROLE_EXTRAS.ADMIN_ETC);

export const userIsPsadStaff = (acc) =>
  userIsStaff(acc) && norm(acc && acc.struct_type) === "psad";

// A PSAD admin is now a full admin (role='admin') of a PSAD structure; the
// admin_psad role_extra is kept as a marker. (Previously these users were stored
// as role='staff' + admin_psad, which excluded them from admin-only logic.)
export const userIsPsadAdmin = (acc) =>
  userIsAdmin(acc) && userHasRoleExtra(acc, ROLE_EXTRAS.ADMIN_PSAD);

// Matches any user (staff or admin) whose structure is of type "psad".
export const userIsInPsadStructure = (acc) =>
  norm(acc && acc.struct_type) === "psad";

// True when the account administrates THAT structure. Mirrors the backend's
// isAdminOfStructure (index.js), which is what authorizes the structure-share
// routes: keep both in sync so the UI never offers an action the API refuses.
export const userIsStructureAdminOf = (acc, id_structure) => {
  if (!acc || id_structure === undefined || id_structure === null) return false;
  if (String(acc.FK_structure) !== String(id_structure)) return false;
  return userIsAdmin(acc)
    || (userIsStaff(acc)
        && (userHasRoleExtra(acc, ROLE_EXTRAS.ADMIN_PSAD) || userHasRoleExtra(acc, ROLE_EXTRAS.ADMIN_ETC)));
};

// A user is considered a practitioner when they have an RPPS-like identifier
// (stored in `username` for practitioners) or a profession / category set.
const isRppsLike = (v) => {
  if (v === undefined || v === null) return false;
  return /^\d{9,11}$/.test(String(v).trim());
};
const hasNonEmpty = (v) =>
  v !== undefined && v !== null && v !== "null" && v !== "";

export const userIsPractitioner = (acc) => {
  if (!acc) return false;
  if (isRppsLike(acc.rpps)) return true;
  if (isRppsLike(acc.username)) return true;
  return hasNonEmpty(acc.profession) || hasNonEmpty(acc.category);
};

export const userRpps = (acc) => {
  if (!acc) return null;
  if (isRppsLike(acc.rpps)) return String(acc.rpps).trim();
  if (isRppsLike(acc.username)) return String(acc.username).trim();
  return null;
};

export const userIsReadOnly = (acc) => !!(acc && acc.readOnly === true);
